import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, FunnelChart, Funnel, LabelList } from 'recharts';
import { PerformanceData } from '@/hooks/usePerformanceData';

interface FunnelVisualizationProps {
  data: PerformanceData | null;
  selectedRecruiterId: string | null;
}

export function FunnelVisualization({ data, selectedRecruiterId }: FunnelVisualizationProps) {
  if (!data) return null;

  // Aggregate funnel data
  const funnelAggregation = new Map<string, { count: number; stageName: string; stageType: string; color: string }>();

  const recruitersToProcess = selectedRecruiterId
    ? data.recruiters.filter((r) => r.recruiterId === selectedRecruiterId)
    : data.recruiters;

  recruitersToProcess.forEach((recruiter) => {
    recruiter.funnelData.forEach((stage) => {
      if (!funnelAggregation.has(stage.stageType)) {
        funnelAggregation.set(stage.stageType, { ...stage, count: 0 });
      }
      const existing = funnelAggregation.get(stage.stageType)!;
      existing.count += stage.count;
    });
  });

  // Order the stages properly
  const stageOrder = ['sourced', 'applied', 'new', 'screening', 'interview', 'technical_interview', 'final_interview', 'offer', 'hired', 'rejected'];
  
  const funnelData = Array.from(funnelAggregation.values())
    .sort((a, b) => {
      const aIndex = stageOrder.indexOf(a.stageType);
      const bIndex = stageOrder.indexOf(b.stageType);
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    })
    .map((stage, index) => ({
      name: stage.stageName,
      value: stage.count,
      fill: stage.color || getDefaultColor(index),
    }));

  // Prepare bar chart data for per-recruiter comparison
  const barChartData = selectedRecruiterId
    ? funnelData
    : data.recruiters
        .filter((r) => r.totalCandidates > 0)
        .map((r) => ({
          name: r.recruiterName.split(' ')[0],
          sourced: r.candidatesSourced,
          interviewed: r.candidatesInterviewed,
          selected: r.candidatesSelected,
        }));

  if (funnelData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Candidate Funnel</CardTitle>
          <CardDescription>Visualization of candidates through hiring stages</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-muted-foreground">
            No funnel data available for the selected period
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Funnel Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Candidate Funnel</CardTitle>
          <CardDescription>
            {selectedRecruiterId ? 'Individual recruiter pipeline' : 'Team-wide pipeline stages'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnelData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {funnelData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Comparison Chart */}
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedRecruiterId ? 'Stage Breakdown' : 'Recruiter Comparison'}
          </CardTitle>
          <CardDescription>
            {selectedRecruiterId
              ? 'Candidates at each stage'
              : 'Compare sourced, interviewed, and selected candidates'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            {selectedRecruiterId ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnelData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {funnelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barChartData as any[]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="sourced" name="Sourced" fill="hsl(239 84% 67%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="interviewed" name="Interviewed" fill="hsl(45 93% 47%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="selected" name="Selected" fill="hsl(142 76% 36%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function getDefaultColor(index: number): string {
  const colors = [
    'hsl(239 84% 67%)',
    'hsl(217 91% 60%)',
    'hsl(199 89% 48%)',
    'hsl(142 76% 36%)',
    'hsl(45 93% 47%)',
    'hsl(0 84% 60%)',
    'hsl(280 65% 60%)',
    'hsl(320 65% 52%)',
  ];
  return colors[index % colors.length];
}
